import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField, Box, Typography, MenuItem, Autocomplete, FormControlLabel, Checkbox, Alert,
} from '@mui/material';
import { RootState, AppDispatch } from '../../store/store';
import { setActiveStep, updateFormData, updateFiles, type OnboardingFiles } from './onboardingSlice';
import FileUpload from '../../components/FileUpload';
import StepNav from './StepNav';
import { fetchCountries } from '../../api/meta';
import {
  SELL_TYPES, isValidUsdAmount, isValidPositiveInt, isValidStatementDescriptor, isValidWebsiteUrl,
} from './kybConstants';

interface Country { id: string; name: string; }

const BusinessDetails = () => {
  const dispatch: AppDispatch = useDispatch();
  const { formData, activeStep, files } = useSelector((state: RootState) => state.onboarding);

  const [sellType, setSellType] = useState(formData.sellType || SELL_TYPES[0]);
  const [tradeName, setTradeName] = useState(formData.tradeName);
  const [businessDescription, setBusinessDescription] = useState(formData.businessDescription);
  const [website, setWebsite] = useState(formData.website);
  const [statementDescriptor, setStatementDescriptor] = useState(formData.statementDescriptor);
  const [maxTransactionUsd, setMaxTransactionUsd] = useState(formData.maxTransactionUsd);
  const [annualRevenueUsd, setAnnualRevenueUsd] = useState(formData.annualRevenueUsd);
  const [avgTransactionUsd, setAvgTransactionUsd] = useState(formData.avgTransactionUsd);
  const [avgFulfillmentDays, setAvgFulfillmentDays] = useState(formData.avgFulfillmentDays);
  const [sameAsRegistered, setSameAsRegistered] = useState(formData.sameAsRegisteredAddress);
  const [physicalStreet, setPhysicalStreet] = useState(formData.physicalStreet);
  const [physicalStreetLine2, setPhysicalStreetLine2] = useState(formData.physicalStreetLine2);
  const [physicalCity, setPhysicalCity] = useState(formData.physicalCity);
  const [physicalProvince, setPhysicalProvince] = useState(formData.physicalProvince);
  const [physicalZip, setPhysicalZip] = useState(formData.physicalZip);
  const [physicalCountry, setPhysicalCountry] = useState<Country | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [touched, setTouched] = useState(false);
  const [metaError, setMetaError] = useState('');

  const commonAccept = 'application/pdf,image/*';
  const maxBytes = 10 * 1024 * 1024;

  useEffect(() => {
    (async () => {
      try {
        const c = await fetchCountries();
        setCountries(c || []);
        if (formData.physicalCountry) {
          const match = (c || []).find((co) => co.id === formData.physicalCountry);
          setPhysicalCountry(match || null);
        }
        setMetaError('');
      } catch (_) {
        setMetaError('Could not load countries. Check your connection and refresh.');
      }
    })();
  }, [formData.physicalCountry]);

  useEffect(() => {
    if (!sameAsRegistered) return;
    setPhysicalStreet(formData.street);
    setPhysicalStreetLine2(formData.streetLine2);
    setPhysicalCity(formData.city);
    setPhysicalProvince(formData.province);
    setPhysicalZip(formData.zip);
    const match = countries.find((c) => c.id === formData.country);
    if (match) setPhysicalCountry(match);
  }, [sameAsRegistered, formData.street, formData.streetLine2, formData.city, formData.province, formData.zip, formData.country, countries]);

  const validFinancials = isValidUsdAmount(maxTransactionUsd)
    && isValidUsdAmount(annualRevenueUsd)
    && isValidUsdAmount(avgTransactionUsd)
    && isValidPositiveInt(avgFulfillmentDays);
  const validAddress = Boolean(physicalStreet.trim() && physicalCity.trim() && physicalZip.trim() && physicalCountry);
  const validDescriptor = isValidStatementDescriptor(statementDescriptor);
  const validDocs = Boolean(files.businessProofOfAddress);
  const validWebsite = isValidWebsiteUrl(website);
  const valid = Boolean(
    sellType && tradeName.trim() && businessDescription.trim() && validWebsite && validDescriptor
    && validFinancials && validAddress && validDocs,
  );

  const missing: string[] = [];
  if (!tradeName.trim()) missing.push('Brand name (trade name)');
  if (!businessDescription.trim()) missing.push('Business description');
  if (!validWebsite) missing.push('Valid website URL');
  if (!validDescriptor) missing.push('Statement descriptor (5–22 characters)');
  if (!isValidUsdAmount(maxTransactionUsd)) missing.push('Highest single transaction');
  if (!isValidUsdAmount(annualRevenueUsd)) missing.push('Approx annual revenue');
  if (!isValidUsdAmount(avgTransactionUsd)) missing.push('Average transaction value');
  if (!isValidPositiveInt(avgFulfillmentDays)) missing.push('Avg order fulfillment time');
  if (!physicalStreet.trim()) missing.push('Physical address line 1');
  if (!physicalCity.trim()) missing.push('Physical city');
  if (!physicalZip.trim()) missing.push('Physical postal code');
  if (!physicalCountry) missing.push('Physical address country (pick from the list)');
  if (!validDocs) missing.push('Proof of address document');

  const handleNext = () => {
    setTouched(true);
    if (!valid) return;
    dispatch(updateFormData({
      sellType,
      tradeName: tradeName.trim(),
      businessDescription: businessDescription.trim(),
      website: website.trim(),
      statementDescriptor: statementDescriptor.trim(),
      maxTransactionUsd,
      annualRevenueUsd,
      avgTransactionUsd,
      avgFulfillmentDays,
      sameAsRegisteredAddress: sameAsRegistered,
      physicalStreet,
      physicalStreetLine2,
      physicalCity,
      physicalProvince,
      physicalZip,
      physicalCountry: physicalCountry?.id || '',
    }));
    dispatch(setActiveStep(activeStep + 1));
  };

  const handleBack = () => dispatch(setActiveStep(activeStep - 1));

  return (
    <>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        We need a bit more about your business for compliance. This must be completed by a senior executive.
      </Typography>

      <TextField select label="How do you intend to sell?" fullWidth margin="normal" value={sellType} onChange={(e) => setSellType(e.target.value)} required>
        {SELL_TYPES.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
      </TextField>

      <TextField label="Brand Name (trade name)" fullWidth margin="normal" value={tradeName} onChange={(e) => setTradeName(e.target.value)} required error={touched && !tradeName.trim()} helperText={touched && !tradeName.trim() ? 'Brand name is required' : ''} />
      <TextField label="Business Description" fullWidth margin="normal" multiline minRows={3} value={businessDescription} onChange={(e) => setBusinessDescription(e.target.value)} required error={touched && !businessDescription.trim()} helperText={touched && !businessDescription.trim() ? 'Business description is required' : 'Brief description of what the business does'} />
      <TextField
        label="Website URL"
        fullWidth
        margin="normal"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        placeholder="https://example.com"
        required
        error={touched && !validWebsite}
        helperText={
          touched && !validWebsite
            ? 'Enter a valid website URL (e.g. https://example.com)'
            : 'Required for compliance. If you do not have a public site (e.g. restaurant, hotel, showroom), enter a booking page, Google Business profile, or social page URL.'
        }
      />
      <TextField label="Statement Descriptor" fullWidth margin="normal" value={statementDescriptor} onChange={(e) => setStatementDescriptor(e.target.value)} required inputProps={{ maxLength: 22 }} error={touched && !validDescriptor} helperText={touched && !validDescriptor ? 'Must be 5–22 characters' : 'Shows on customer bank/card statements (5–22 characters)'} />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Financial expectations (USD)</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextField label="Highest single transaction (USD)" fullWidth value={maxTransactionUsd} onChange={(e) => setMaxTransactionUsd(e.target.value.replace(/[^\d.]/g, ''))} required error={touched && !isValidUsdAmount(maxTransactionUsd)} helperText={touched && !isValidUsdAmount(maxTransactionUsd) ? 'Enter a valid number' : ''} />
        <TextField label="Approx annual revenue (USD)" fullWidth value={annualRevenueUsd} onChange={(e) => setAnnualRevenueUsd(e.target.value.replace(/[^\d.]/g, ''))} required error={touched && !isValidUsdAmount(annualRevenueUsd)} />
        <TextField label="Average transaction value (USD)" fullWidth value={avgTransactionUsd} onChange={(e) => setAvgTransactionUsd(e.target.value.replace(/[^\d.]/g, ''))} required error={touched && !isValidUsdAmount(avgTransactionUsd)} />
        <TextField label="Avg order fulfillment time (days)" fullWidth value={avgFulfillmentDays} onChange={(e) => setAvgFulfillmentDays(e.target.value.replace(/\D/g, ''))} required error={touched && !isValidPositiveInt(avgFulfillmentDays)} />
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Physical business address</Typography>
      <FormControlLabel control={<Checkbox checked={sameAsRegistered} onChange={(e) => setSameAsRegistered(e.target.checked)} />} label="Same as registered company address" />
      {metaError && <Alert severity="error" sx={{ mt: 1 }}>{metaError}</Alert>}
      <TextField label="Address Line 1" fullWidth margin="normal" value={physicalStreet} onChange={(e) => setPhysicalStreet(e.target.value)} required disabled={sameAsRegistered} error={touched && !physicalStreet.trim()} />
      <TextField label="Address Line 2" fullWidth margin="normal" value={physicalStreetLine2} onChange={(e) => setPhysicalStreetLine2(e.target.value)} disabled={sameAsRegistered} />
      <TextField label="City" fullWidth margin="normal" value={physicalCity} onChange={(e) => setPhysicalCity(e.target.value)} required disabled={sameAsRegistered} error={touched && !physicalCity.trim()} />
      <TextField label="State / Province" fullWidth margin="normal" value={physicalProvince} onChange={(e) => setPhysicalProvince(e.target.value)} disabled={sameAsRegistered} />
      <TextField label="Postal Code" fullWidth margin="normal" value={physicalZip} onChange={(e) => setPhysicalZip(e.target.value)} required disabled={sameAsRegistered} error={touched && !physicalZip.trim()} />
      <Autocomplete
        options={countries}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={physicalCountry}
        onChange={(_e, v) => setPhysicalCountry(v)}
        disabled={sameAsRegistered}
        renderInput={(params) => <TextField {...params} label="Address Country" margin="normal" required error={touched && !physicalCountry} helperText={touched && !physicalCountry ? 'Select a country from the list' : ''} />}
      />

      <Box sx={{ mt: 2 }}>
        <FileUpload
          label="Proof of Address Document"
          onFileSelect={(file: File) => dispatch(updateFiles({ businessProofOfAddress: file } as Partial<OnboardingFiles>))}
          selectedFile={files.businessProofOfAddress}
          accept={commonAccept}
          maxBytes={maxBytes}
          helperText="Utility bill or bank statement. PDF or image, max 10MB"
          required
          error={touched && !validDocs ? 'Proof of address is required' : null}
        />
      </Box>

      <StepNav onBack={handleBack} onNext={handleNext} missing={missing} showMissing={touched} />
    </>
  );
};

export default BusinessDetails;
